import { useId, type ReactNode, type Ref } from 'react';

/**
 * A round control that shows an icon and says what it is when asked.
 *
 * The name is on the button as its accessible name, and the tooltip repeats it visibly alongside a
 * sentence of description. Both appear on hover **and** on keyboard focus, which is why the tooltip
 * is a sibling of the button rather than a child: text inside a button becomes part of its name,
 * and a name that grows a whole sentence is a worse name.
 *
 * No JavaScript is involved in showing it. `:hover` and `:focus-within` on the wrapper do the whole
 * job, so the tooltip cannot get stuck open, cannot be left behind by a re-render, and costs
 * nothing on a device that has no pointer at all.
 */
export function IconButton({
  name,
  description,
  icon,
  pressed,
  expanded,
  controls,
  onClick,
  ref,
}: {
  readonly name: string;
  readonly description: string;
  readonly icon: ReactNode;
  /** For a toggle: renders the on state and announces it. Omit for a button that opens something. */
  readonly pressed?: boolean;
  readonly expanded?: boolean;
  readonly controls?: string;
  readonly onClick: () => void;
  readonly ref?: Ref<HTMLButtonElement>;
}) {
  const tipId = useId();

  return (
    <span className="icon-button">
      <button
        type="button"
        className={`icon-button__control${pressed === true ? ' icon-button__control--on' : ''}`}
        aria-label={name}
        aria-describedby={tipId}
        {...(pressed !== undefined && { 'aria-pressed': pressed })}
        {...(expanded !== undefined && { 'aria-expanded': expanded })}
        {...(controls !== undefined && { 'aria-controls': controls })}
        onClick={onClick}
        ref={ref}
      >
        {icon}
      </button>

      <span className="icon-button__tip" role="tooltip" id={tipId}>
        <span className="icon-button__tip-name">{name}</span>
        <span className="icon-button__tip-text">{description}</span>
      </span>
    </span>
  );
}
